import React from 'react'
import { Shield, Award, Users, Clock, Truck, Lock } from 'lucide-react'

interface CertificationItemProps {
	icon: React.ReactNode
	title: string
	description: string
	highlight?: string
}

const CertificationItem: React.FC<CertificationItemProps> = ({
	icon,
	title,
	description,
	highlight,
}) => {
	return (
		<div className='group relative bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 animate-pulse hover:animate-none'>
			<div className='absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-700' />

			<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700' />

			<div className='relative p-4 sm:p-6 lg:p-8 text-center'>
				<div className='relative mx-auto mb-4 sm:mb-6'>
					<div className='w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto rounded-full bg-gradient-to-br from-gray-100 to-gray-200 p-1 shadow-inner'>
						<div className='w-full h-full rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300'>
							<div className='text-white transform group-hover:rotate-12 transition-transform duration-300'>
								{icon}
							</div>
						</div>
					</div>

					<div className='absolute inset-0 rounded-full bg-red-500/20 opacity-0 group-hover:opacity-75 animate-ping transition-opacity duration-300' />
				</div>

				<div className='space-y-2 sm:space-y-3'>
					<h3 className='text-lg sm:text-xl lg:text-2xl font-black text-gray-800 leading-tight group-hover:text-red-600 transition-colors duration-300'>
						{title}
					</h3>

					{highlight && (
						<div className='inline-block bg-gradient-to-r from-red-50 to-red-100 text-red-700 font-bold text-sm sm:text-lg lg:text-xl px-3 py-1 sm:px-4 sm:py-2 rounded-full border border-red-200'>
							{highlight}
						</div>
					)}

					<p className='text-gray-600 text-sm sm:text-base lg:text-lg font-medium leading-relaxed'>
						{description}
					</p>
				</div>

				<div className='mt-4 sm:mt-6 flex items-center justify-center space-x-1'>
					{[...Array(5)].map((_, i) => (
						<div
							key={i}
							className='w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500 opacity-80 animate-pulse'
						/>
					))}
				</div>
			</div>
		</div>
	)
}

const Certifications: React.FC = () => {
	const certifications = [
		{
			icon: <Shield size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Confianza Garantizada',
			highlight: '+5000',
			description:
				'Clientes satisfechos que respaldan nuestro compromiso con un servicio transparente y responsable.',
		},
		{
			icon: <Users size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Comunidad Activa',
			highlight: '+10k',
			description:
				'Una red creciente de personas que confían en nuestros productos y servicios cada día.',
		},
		{
			icon: <Lock size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Seguridad',
			highlight: '100%',
			description:
				'Manejo responsable y seguro de tus datos e información en cada transacción.',
		},
		{
			icon: <Award size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Calidad Reconocida',
			highlight: 'Top',
			description:
				'Procesos internos enfocados en mantener la excelencia en cada producto y servicio.',
		},
		{
			icon: <Truck size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Alcance Nacional',
			highlight: 'Colombia',
			description:
				'Envíos a todo el país en 24 a 72 horas, garantizando rapidez y confianza en cada entrega.',
		},
		{
			icon: <Clock size={24} className='sm:w-8 sm:h-8 lg:w-9 lg:h-9' />,
			title: 'Trayectoria',
			highlight: '+4 años',
			description:
				'Experiencia que respalda nuestra evolución constante y crecimiento junto a nuestros clientes.',
		},
	]

	return (
		<section className='w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden'>
			<div className='max-w-7xl mx-auto mb-8 sm:mb-12 lg:mb-16 text-center'>
				<div className='inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide mb-4 sm:mb-6 shadow-lg'>
					✓ SportCenter Certificado
				</div>

				<h2 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-800 mb-3 sm:mb-4 leading-tight'>
					Tu Confianza es Nuestra
					<span className='block text-red-600'>Certificación</span>
				</h2>

				<p className='text-base sm:text-lg lg:text-xl text-gray-600 font-medium max-w-4xl mx-auto leading-relaxed'>
					Comprometidos con la excelencia, la seguridad y tu satisfacción total
				</p>

				<div className='flex items-center justify-center mt-6 sm:mt-8 mb-8 sm:mb-12'>
					<div className='h-1 w-16 sm:w-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full' />
					<div className='w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full mx-3 sm:mx-4' />
					<div className='h-1 w-16 sm:w-20 bg-gradient-to-l from-red-500 to-red-600 rounded-full' />
				</div>
			</div>

			<div className='max-w-7xl mx-auto'>
				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8'>
					{certifications.map((cert, index) => (
						<CertificationItem
							key={index}
							icon={cert.icon}
							title={cert.title}
							highlight={cert.highlight}
							description={cert.description}
						/>
					))}
				</div>
			</div>

			<div className='max-w-5xl mx-auto mt-12 sm:mt-16 lg:mt-20 text-center'>
				<div className='bg-white rounded-2xl border border-gray-200 shadow-lg p-6 sm:p-8 lg:p-10'>
					<div className='flex items-center justify-center mb-4 sm:mb-6'>
						<div className='flex items-center space-x-2'>
							<Shield className='w-6 h-6 sm:w-8 sm:h-8 text-red-600' />
							<span className='text-xl sm:text-2xl lg:text-3xl font-black text-gray-800'>
								SportCenter Shop
							</span>
						</div>
					</div>

					<p className='text-base sm:text-lg lg:text-xl font-semibold text-gray-700 mb-3 sm:mb-4'>
						Más de{' '}
						<span className='text-red-600 font-black'>
							15,000 clientes satisfechos
						</span>
					</p>

					<p className='text-sm sm:text-base lg:text-lg text-gray-600 font-medium leading-relaxed'>
						Únete a la comunidad fitness más grande de Colombia.
						<span className='block mt-2 text-red-600 font-semibold'>
							Tu transformación comienza aquí 💪
						</span>
					</p>

					<div className='flex items-center justify-center mt-4 sm:mt-6 space-x-1 flex-wrap'>
						{[...Array(5)].map((_, i) => (
							<div key={i} className='w-5 h-5 sm:w-6 sm:h-6 text-yellow-400'>
								⭐
							</div>
						))}
						<span className='ml-2 sm:ml-3 text-sm sm:text-base text-gray-600 font-semibold'>
							4.9/5 en reseñas
						</span>
					</div>
				</div>
			</div>
		</section>
	)
}

export default Certifications
